import { Routes } from '@angular/router';

import {
  FEATURE_ACCESS_ABSENCES_CREATE,
  FEATURE_ACCESS_ABSENCE_TYPES_CREATE,
  FEATURE_ACCESS_ABSENCE_TYPES_UPDATE,
  FEATURE_ACCESS_ACTIVITIES_CREATE,
  FEATURE_ACCESS_ACTIVITIES_READ,
  FEATURE_ACCESS_ACTIVITIES_UPDATE,
  FEATURE_ACCESS_ADVANCED_REPORTS_READ,
  FEATURE_ACCESS_ABSENCES_READ,
  FEATURE_ACCESS_ABSENCES_UPDATE,
  FEATURE_ACCESS_CONTRACT_TYPES_CREATE,
  FEATURE_ACCESS_CONTRACT_TYPES_READ,
  FEATURE_ACCESS_CONTRACT_TYPES_UPDATE,
  FEATURE_ACCESS_ATTACHMENTS_CREATE,
  FEATURE_ACCESS_ATTACHMENTS_READ,
  FEATURE_ACCESS_OVERTIME_RULES_READ,
  FEATURE_ACCESS_PROJECTS_CREATE,
  FEATURE_ACCESS_PROJECTS_READ,
  FEATURE_ACCESS_PROJECTS_UPDATE,
  FEATURE_ACCESS_PROJECTS_USERS_READ,
  FEATURE_ACCESS_REQUESTS_READ,
  FEATURE_ACCESS_REQUESTS_CREATE,
  FEATURE_ACCESS_REQUESTS_REPLY_CREATE,
  FEATURE_ACCESS_REQUESTS_UPDATE,
  FEATURE_ACCESS_SITES_CREATE,
  FEATURE_ACCESS_SITES_READ,
  FEATURE_ACCESS_SITES_UPDATE,
  FEATURE_ACCESS_SITES_USERS_READ,
  FEATURE_ACCESS_OVERTIME_APPROVE,
  FEATURE_ACCESS_OVERTIME_LOCK,
  FEATURE_ACCESS_OVERTIME_READ,
  FEATURE_ACCESS_SELF_BI_MANAGE,
  FEATURE_ACCESS_SELF_BI_READ,
  FEATURE_ACCESS_SHIFTS_ASSIGN,
  FEATURE_ACCESS_SHIFTS_ATTENDANCE_READ,
  FEATURE_ACCESS_SHIFTS_CALENDAR_READ,
  FEATURE_ACCESS_SHIFTS_MANAGE,
  FEATURE_ACCESS_SHIFTS_READ,
  FEATURE_ACCESS_STATS_ABSENCES_READ,
  FEATURE_ACCESS_STATS_WORKS_READ,
  FEATURE_ACCESS_USERS_CREATE,
  FEATURE_ACCESS_USERS_CONTRACTS_CREATE,
  FEATURE_ACCESS_USERS_CONTRACTS_READ,
  FEATURE_ACCESS_USERS_CONTRACTS_UPDATE,
  FEATURE_ACCESS_USERS_PERMISSIONS_READ,
  FEATURE_ACCESS_USERS_ROLES_READ,
  FEATURE_ACCESS_USERS_READ,
  FEATURE_ACCESS_USERS_UPDATE,
  FEATURE_ACCESS_WORKS_MANUAL_CREATE,
  FEATURE_ACCESS_WORKS_UPDATE,
  FEATURE_ACCESS_WORKS_READ
} from './core/auth/feature-access.registry';
import { authGuard } from './core/guards/auth.guard';
import { featureGuard } from './core/guards/feature.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/public/login/login.page').then((m) => m.LoginPage)
  },
  {
    path: 'registrazione',
    loadComponent: () =>
      import('./features/public/registration/registration.page').then((m) => m.RegistrationPage)
  },
  {
    path: 'registrazione/verifica-email',
    loadComponent: () =>
      import('./features/public/registration/email-verification-page').then(
        (m) => m.EmailVerificationPage
      )
  },
  {
    path: 'registrazione/completa',
    loadComponent: () =>
      import('./features/public/registration/completa.page').then(
        (m) => m.CompletaRegistrazionePage
      )
  },
  {
    path: 'password-dimenticata',
    loadComponent: () =>
      import('./features/public/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPage
      )
  },
  {
    path: 'reimposta-password',
    loadComponent: () =>
      import('./features/public/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage
      )
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/shell/shell-layout.page').then((m) => m.ShellLayoutPage),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/home/home.page').then((m) => m.HomePage)
      },
      {
        path: 'home',
        loadComponent: () => import('./features/home/home.page').then((m) => m.HomePage)
      },
      {
        path: 'logout',
        canActivate: [permissionGuard],
        data: { access: 'auth.logout' },
        loadComponent: () => import('./features/public/logout/logout.page').then((m) => m.LogoutPage)
      },
      {
        path: 'profilo',
        canActivate: [permissionGuard],
        data: { access: 'auth.me.read' },
        loadComponent: () => import('./features/profile/profile.page').then((m) => m.ProfilePage)
      },
      {
        path: 'cambio-password',
        canActivate: [permissionGuard],
        data: { access: 'auth.change_password' },
        loadComponent: () =>
          import('./features/profile/change-password.page').then((m) => m.ChangePasswordPage)
      },
      {
        path: 'azienda',
        canActivate: [permissionGuard],
        data: { access: 'tenant.company.read' },
        loadComponent: () =>
          import('./features/companies/company-detail.page').then((m) => m.CompanyDetailPage)
      },
      {
        path: 'azienda/modifica',
        canActivate: [permissionGuard],
        data: { access: 'tenant.company.update' },
        loadComponent: () =>
          import('./features/companies/company-edit.page').then((m) => m.CompanyEditPage)
      },
      {
        path: 'azienda/stati',
        canActivate: [permissionGuard],
        data: { access: 'tenant.company.lifecycle.read' },
        loadComponent: () =>
          import('./features/companies/company-lifecycle-list.page').then((m) => m.CompanyLifecycleListPage)
      },
      {
        path: 'azienda/stati/nuovo',
        canActivate: [permissionGuard],
        data: { access: 'platform.company.lifecycle.manage' },
        loadComponent: () =>
          import('./features/companies/company-lifecycle-create.page').then((m) => m.CompanyLifecycleCreatePage)
      },
      {
        path: 'azienda/offerta',
        canActivate: [permissionGuard],
        data: { access: { allOf: ['tenant.pricing.company.read', 'tenant.payments.read'] } },
        loadComponent: () =>
          import('./features/companies/company-offer.page').then((m) => m.CompanyOfferPage)
      },
      {
        path: 'azienda/export',
        canActivate: [permissionGuard, featureGuard],
        data: { access: 'tenant.exports.read', features: ['DATA_EXPORT_BASE'] },
        loadComponent: () =>
          import('./features/export/export-jobs.page').then((m) => m.ExportJobsPage)
      },
      {
        path: 'azienda/listino',
        pathMatch: 'full',
        redirectTo: 'azienda/offerta'
      },
      {
        path: 'azienda/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'COMPANY',
            useCurrentCompany: true,
            title: 'Allegati Azienda'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'azienda/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'COMPANY',
            useCurrentCompany: true,
            title: 'Nuovo Allegato Azienda'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'aziende/nuova',
        canActivate: [permissionGuard],
        data: { access: 'platform.companies.create' },
        loadComponent: () =>
          import('./features/companies/company-create.page').then((m) => m.CompanyCreatePage)
      },
      {
        path: 'aziende',
        canActivate: [permissionGuard],
        data: { access: 'platform.companies.read' },
        loadComponent: () =>
          import('./features/companies/companies-list.page').then((m) => m.CompaniesListPage)
      },
      {
        path: 'aziende/:companyId',
        canActivate: [permissionGuard],
        data: { access: 'platform.companies.read' },
        loadComponent: () =>
          import('./features/companies/company-detail.page').then((m) => m.CompanyDetailPage)
      },
      {
        path: 'aziende/:companyId/modifica',
        canActivate: [permissionGuard],
        data: { access: 'platform.companies.update' },
        loadComponent: () =>
          import('./features/companies/company-edit.page').then((m) => m.CompanyEditPage)
      },
      {
        path: 'aziende/:companyId/stati',
        canActivate: [permissionGuard],
        data: { access: { allOf: ['tenant.company.lifecycle.read', 'platform.companies.read'] } },
        loadComponent: () =>
          import('./features/companies/company-lifecycle-list.page').then((m) => m.CompanyLifecycleListPage)
      },
      {
        path: 'aziende/:companyId/stati/nuovo',
        canActivate: [permissionGuard],
        data: { access: { allOf: ['platform.company.lifecycle.manage', 'platform.companies.read'] } },
        loadComponent: () =>
          import('./features/companies/company-lifecycle-create.page').then((m) => m.CompanyLifecycleCreatePage)
      },
      {
        path: 'aziende/:companyId/offerta',
        canActivate: [permissionGuard],
        data: { access: { allOf: ['platform.companies.read', 'platform.pricing.company.read', 'platform.billing.ops.read'] } },
        loadComponent: () =>
          import('./features/companies/company-offer.page').then((m) => m.CompanyOfferPage)
      },
      {
        path: 'aziende/:companyId/listino',
        pathMatch: 'full',
        redirectTo: 'aziende/:companyId/offerta'
      },
      {
        path: 'listini',
        canActivate: [permissionGuard],
        data: { access: 'platform.pricing.master.read' },
        loadComponent: () =>
          import('./features/price-lists/price-lists-list.page').then((m) => m.PriceListsListPage)
      },
      {
        path: 'listini/nuovo',
        canActivate: [permissionGuard],
        data: { access: 'platform.pricing.master.create' },
        loadComponent: () =>
          import('./features/price-lists/price-list-create.page').then((m) => m.PriceListCreatePage)
      },
      {
        path: 'listini/:priceListId',
        canActivate: [permissionGuard],
        data: { access: 'platform.pricing.master.read' },
        loadComponent: () =>
          import('./features/price-lists/price-list-detail.page').then((m) => m.PriceListDetailPage)
      },
      {
        path: 'listini/:priceListId/modifica',
        canActivate: [permissionGuard],
        data: { access: 'platform.pricing.master.update' },
        loadComponent: () =>
          import('./features/price-lists/price-list-edit.page').then((m) => m.PriceListEditPage)
      },
      {
        path: 'listini/:priceListId/fasce',
        canActivate: [permissionGuard],
        data: { access: 'platform.pricing.master.read' },
        loadComponent: () =>
          import('./features/price-lists/price-list-tiers.page').then((m) => m.PriceListTiersPage)
      },
      {
        path: 'azienda/billing',
        canActivate: [permissionGuard],
        data: { access: 'tenant.payments.read' },
        loadComponent: () =>
          import('./features/billing/billing-dashboard.page').then((m) => m.BillingDashboardPage)
      },
      {
        path: 'aziende/:companyId/billing',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.ops.read' },
        loadComponent: () =>
          import('./features/billing/billing-dashboard.page').then((m) => m.BillingDashboardPage)
      },
      {
        path: 'piattaforma/billing/ops',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.ops.read' },
        loadComponent: () =>
          import('./features/billing/billing-ops.page').then((m) => m.BillingOpsPage)
      },
      {
        path: 'piattaforma/billing/pagamenti',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.payments.read' },
        loadComponent: () =>
          import('./features/billing/billing-platform-payments.page').then((m) => m.BillingPlatformPaymentsPage)
      },
      {
        path: 'piattaforma/billing/pagamenti/:paymentId',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.payments.read' },
        loadComponent: () =>
          import('./features/billing/billing-payment-detail.page').then((m) => m.BillingPaymentDetailPage)
      },
      {
        path: 'piattaforma/billing/fatturazione-log',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.invoicing.read' },
        loadComponent: () =>
          import('./features/billing/billing-invoicing-calls.page').then((m) => m.BillingInvoicingCallsPage)
      },
      {
        path: 'piattaforma/billing/fatturazione-log/:callLogId',
        canActivate: [permissionGuard],
        data: { access: 'platform.billing.invoicing.read' },
        loadComponent: () =>
          import('./features/billing/billing-invoicing-call-detail.page').then((m) => m.BillingInvoicingCallDetailPage)
      },
      {
        path: 'billing',
        pathMatch: 'full',
        redirectTo: 'azienda/billing'
      },
      {
        path: 'billing/payments/:paymentId',
        canActivate: [permissionGuard],
        data: { access: 'tenant.payments.read' },
        loadComponent: () =>
          import('./features/billing/billing-payment-detail.page').then((m) => m.BillingPaymentDetailPage)
      },
      {
        path: 'billing/payments/:paymentId/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'PAYMENT',
            paramKey: 'paymentId',
            title: 'Allegati Pagamento'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'billing/payments/:paymentId/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'PAYMENT',
            paramKey: 'paymentId',
            title: 'Nuovo Allegato Pagamento'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'billing/payment/:result',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['tenant.billing.methods.manage', 'platform.billing.methods.manage'] } },
        loadComponent: () =>
          import('./features/billing/billing-payment-return.page').then((m) => m.BillingPaymentReturnPage)
      },
      {
        path: 'utenti',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_READ },
        loadComponent: () =>
          import('./features/users/users-list.page').then((m) => m.UsersListPage)
      },
      {
        path: 'utenti/nuovo',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_CREATE },
        loadComponent: () =>
          import('./features/users/user-create.page').then((m) => m.UserCreatePage)
      },
      {
        path: 'utenti/:userId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_READ },
        loadComponent: () =>
          import('./features/users/user-detail.page').then((m) => m.UserDetailPage)
      },
      {
        path: 'utenti/:userId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_UPDATE },
        loadComponent: () =>
          import('./features/users/user-edit.page').then((m) => m.UserEditPage)
      },
      {
        path: 'utenti/:userId/ruoli',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_ROLES_READ },
        loadComponent: () =>
          import('./features/users/user-roles.page').then((m) => m.UserRolesPage)
      },
      {
        path: 'utenti/:userId/permessi',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_PERMISSIONS_READ },
        loadComponent: () =>
          import('./features/users/user-permissions.page').then((m) => m.UserPermissionsPage)
      },
      {
        path: 'utenti/:userId/sedi',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_USERS_READ },
        loadComponent: () =>
          import('./features/users/user-sites.page').then((m) => m.UserSitesPage)
      },
      {
        path: 'utenti/:userId/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'USER',
            paramKey: 'userId',
            title: 'Allegati Utente'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'utenti/:userId/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'USER',
            paramKey: 'userId',
            title: 'Nuovo Allegato Utente'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'utenti/:userId/commesse',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_USERS_READ },
        loadComponent: () =>
          import('./features/users/user-projects.page').then((m) => m.UserProjectsPage)
      },
      {
        path: 'utenti/:userId/commesse/:projectId/sedi',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_USERS_READ },
        loadComponent: () =>
          import('./features/users/user-project-sites.page').then((m) => m.UserProjectSitesPage)
      },
      {
        path: 'utenti/:userId/contratti',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_CONTRACTS_READ },
        loadComponent: () =>
          import('./features/users/user-contracts-list.page').then((m) => m.UserContractsListPage)
      },
      {
        path: 'utenti/:userId/contratti/nuovo',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_CONTRACTS_CREATE },
        loadComponent: () =>
          import('./features/users/user-contract-create.page').then((m) => m.UserContractCreatePage)
      },
      {
        path: 'utenti/:userId/contratti/:contractId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_CONTRACTS_READ },
        loadComponent: () =>
          import('./features/users/user-contract-detail.page').then((m) => m.UserContractDetailPage)
      },
      {
        path: 'utenti/:userId/contratti/:contractId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_USERS_CONTRACTS_UPDATE },
        loadComponent: () =>
          import('./features/users/user-contract-edit.page').then((m) => m.UserContractEditPage)
      },
      {
        path: 'utenti/:userId/contratti/:contractId/straordinari',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_OVERTIME_RULES_READ, features: ['ADVANCED_OVERTIME'] },
        loadComponent: () =>
          import('./features/overtime/user-contract-overtime.page').then((m) => m.UserContractOvertimePage)
      },
      {
        path: 'tipi-contratto',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_CONTRACT_TYPES_READ },
        loadComponent: () =>
          import('./features/contract-types/contract-types-list.page').then((m) => m.ContractTypesListPage)
      },
      {
        path: 'tipi-contratto/nuovo',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_CONTRACT_TYPES_CREATE },
        loadComponent: () =>
          import('./features/contract-types/contract-type-create.page').then((m) => m.ContractTypeCreatePage)
      },
      {
        path: 'tipi-contratto/:contractTypeId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_CONTRACT_TYPES_READ },
        loadComponent: () =>
          import('./features/contract-types/contract-type-detail.page').then((m) => m.ContractTypeDetailPage)
      },
      {
        path: 'tipi-contratto/:contractTypeId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_CONTRACT_TYPES_UPDATE },
        loadComponent: () =>
          import('./features/contract-types/contract-type-edit.page').then((m) => m.ContractTypeEditPage)
      },
      {
        path: 'tipi-contratto/:contractTypeId/straordinari',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_OVERTIME_RULES_READ, features: ['ADVANCED_OVERTIME'] },
        loadComponent: () =>
          import('./features/overtime/contract-type-overtime.page').then((m) => m.ContractTypeOvertimePage)
      },
      {
        path: 'permessi',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.roles.permissions.read', 'tenant.permissions.read'] } },
        loadComponent: () =>
          import('./features/role-permissions/permissions-list.page').then((m) => m.PermissionsListPage)
      },
      {
        path: 'ruoli',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.roles.read', 'tenant.roles.read'] } },
        loadComponent: () =>
          import('./features/role-permissions/roles-list.page').then((m) => m.RolesListPage)
      },
      {
        path: 'ruoli/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: { anyOf: ['platform.roles.create', 'tenant.roles.create'] }, features: ['USERS_RBAC_MODULE'] },
        loadComponent: () =>
          import('./features/role-permissions/role-create.page').then((m) => m.RoleCreatePage)
      },
      {
        path: 'ruoli/:roleId',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.roles.read', 'tenant.roles.read'] } },
        loadComponent: () =>
          import('./features/role-permissions/role-detail.page').then((m) => m.RoleDetailPage)
      },
      {
        path: 'ruoli/:roleId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: { access: { anyOf: ['platform.roles.update', 'tenant.roles.update'] }, features: ['USERS_RBAC_MODULE'] },
        loadComponent: () =>
          import('./features/role-permissions/role-edit.page').then((m) => m.RoleEditPage)
      },
      {
        path: 'ruoli/:roleId/permessi',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.roles.permissions.read', 'tenant.roles.permissions.read'] } },
        loadComponent: () =>
          import('./features/role-permissions/role-permissions.page').then((m) => m.RolePermissionsPage)
      },
      {
        path: 'commesse',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_READ },
        loadComponent: () =>
          import('./features/projects/projects-list.page').then((m) => m.ProjectsListPage)
      },
      {
        path: 'commesse/nuova',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_CREATE },
        loadComponent: () =>
          import('./features/projects/project-create.page').then((m) => m.ProjectCreatePage)
      },
      {
        path: 'commesse/:projectId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_UPDATE },
        loadComponent: () =>
          import('./features/projects/project-edit.page').then((m) => m.ProjectEditPage)
      },
      {
        path: 'commesse/:projectId/utenti',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_USERS_READ },
        loadComponent: () =>
          import('./features/projects/project-users.page').then((m) => m.ProjectUsersPage)
      },
      {
        path: 'commesse/:projectId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_PROJECTS_READ },
        loadComponent: () =>
          import('./features/projects/project-detail.page').then((m) => m.ProjectDetailPage)
      },
      {
        path: 'sedi',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_READ },
        loadComponent: () => import('./features/sites/sites-list.page').then((m) => m.SitesListPage)
      },
      {
        path: 'sedi/nuova',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_CREATE },
        loadComponent: () => import('./features/sites/site-create.page').then((m) => m.SiteCreatePage)
      },
      {
        path: 'sedi/:siteId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_UPDATE },
        loadComponent: () => import('./features/sites/site-edit.page').then((m) => m.SiteEditPage)
      },
      {
        path: 'sedi/:siteId/utenti',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_USERS_READ },
        loadComponent: () => import('./features/sites/site-users.page').then((m) => m.SiteUsersPage)
      },
      {
        path: 'sedi/:siteId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_SITES_READ },
        loadComponent: () => import('./features/sites/site-detail.page').then((m) => m.SiteDetailPage)
      },
      {
        path: 'attivita',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_ACTIVITIES_READ },
        loadComponent: () =>
          import('./features/activities/activities-list.page').then((m) => m.ActivitiesListPage)
      },
      {
        path: 'attivita/nuova',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_ACTIVITIES_CREATE },
        loadComponent: () =>
          import('./features/activities/activity-create.page').then((m) => m.ActivityCreatePage)
      },
      {
        path: 'attivita/:activityId',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_ACTIVITIES_READ },
        loadComponent: () =>
          import('./features/activities/activity-detail.page').then((m) => m.ActivityDetailPage)
      },
      {
        path: 'attivita/:activityId/modifica',
        canActivate: [permissionGuard],
        data: { access: FEATURE_ACCESS_ACTIVITIES_UPDATE },
        loadComponent: () =>
          import('./features/activities/activity-edit.page').then((m) => m.ActivityEditPage)
      },
      {
        path: 'turni/template',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SHIFTS_READ, features: ['BASIC_SHIFTS'] },
        loadComponent: () =>
          import('./features/shifts/shift-templates.page').then((m) => m.ShiftTemplatesPage)
      },
      {
        path: 'turni/template/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SHIFTS_MANAGE, features: ['BASIC_SHIFTS'] },
        loadComponent: () =>
          import('./features/shifts/shift-template-create.page').then((m) => m.ShiftTemplateCreatePage)
      },
      {
        path: 'turni/assegnazioni',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SHIFTS_READ, features: ['BASIC_SHIFTS'] },
        loadComponent: () =>
          import('./features/shifts/shift-assignments.page').then((m) => m.ShiftAssignmentsPage)
      },
      {
        path: 'turni/assegnazioni/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SHIFTS_ASSIGN, features: ['BASIC_SHIFTS'] },
        loadComponent: () =>
          import('./features/shifts/shift-assignment-create.page').then((m) => m.ShiftAssignmentCreatePage)
      },
      {
        path: 'turni/calendario',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_SHIFTS_CALENDAR_READ,
          features: ['BASIC_SHIFTS']
        },
        loadComponent: () =>
          import('./features/shifts/shift-calendar.page').then((m) => m.ShiftCalendarPage)
      },
      {
        path: 'turni/consuntivo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SHIFTS_ATTENDANCE_READ, features: ['BASIC_SHIFTS'] },
        loadComponent: () =>
          import('./features/shifts/shift-attendance.page').then((m) => m.ShiftAttendancePage)
      },
      {
        path: 'straordinari',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_OVERTIME_READ, features: ['ADVANCED_OVERTIME'] },
        loadComponent: () =>
          import('./features/overtime/overtime-entries.page').then((m) => m.OvertimeEntriesPage)
      },
      {
        path: 'straordinari/approvazioni',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_OVERTIME_APPROVE, features: ['ADVANCED_OVERTIME'] },
        loadComponent: () =>
          import('./features/overtime/overtime-approvals.page').then((m) => m.OvertimeApprovalsPage)
      },
      {
        path: 'straordinari/periodi',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_OVERTIME_LOCK, features: ['ADVANCED_OVERTIME'] },
        loadComponent: () =>
          import('./features/overtime/overtime-periods.page').then((m) => m.OvertimePeriodsPage)
      },
      {
        path: 'timbrature/in-corso',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_WORKS_READ, features: ['TIMBRATURE_WEB_APP'] },
        loadComponent: () =>
          import('./features/works/current-work.page').then((m) => m.CurrentWorkPage)
      },
      {
        path: 'timbrature',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_WORKS_READ, features: ['TIMBRATURE_WEB_APP'] },
        loadComponent: () =>
          import('./features/works/works-list.page').then((m) => m.WorksListPage)
      },
      {
        path: 'timbrature/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_WORKS_MANUAL_CREATE, features: ['TIMBRATURE_WEB_APP'] },
        loadComponent: () =>
          import('./features/works/work-create.page').then((m) => m.WorkCreatePage)
      },
      {
        path: 'timbrature/:workId',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_WORKS_READ, features: ['TIMBRATURE_WEB_APP'] },
        loadComponent: () =>
          import('./features/works/work-detail.page').then((m) => m.WorkDetailPage)
      },
      {
        path: 'timbrature/:workId/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['TIMBRATURE_WEB_APP', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'WORK',
            paramKey: 'workId',
            title: 'Allegati Timbratura'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'timbrature/:workId/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['TIMBRATURE_WEB_APP', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'WORK',
            paramKey: 'workId',
            title: 'Nuovo Allegato Timbratura'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'timbrature/:workId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_WORKS_UPDATE, features: ['TIMBRATURE_WEB_APP'] },
        loadComponent: () =>
          import('./features/works/work-edit.page').then((m) => m.WorkEditPage)
      },
      {
        path: 'tipi-assenza',
        canActivate: [permissionGuard, featureGuard],
        data: { access: { anyOf: ['platform.absence_types.read', 'tenant.absence_types.read'] }, features: ['ABSENCES'] },
        loadComponent: () =>
          import('./features/absences/absence-types-list.page').then((m) => m.AbsenceTypesListPage)
      },
      {
        path: 'tipi-assenza/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ABSENCE_TYPES_CREATE, features: ['ABSENCES'] },
        loadComponent: () =>
          import('./features/absences/absence-type-create.page').then((m) => m.AbsenceTypeCreatePage)
      },
      {
        path: 'tipi-assenza/:absenceTypeId',
        canActivate: [permissionGuard, featureGuard],
        data: { access: { anyOf: ['platform.absence_types.read', 'tenant.absence_types.read'] }, features: ['ABSENCES'] },
        loadComponent: () =>
          import('./features/absences/absence-type-detail.page').then((m) => m.AbsenceTypeDetailPage)
      },
      {
        path: 'tipi-assenza/:absenceTypeId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ABSENCE_TYPES_UPDATE, features: ['ABSENCES'] },
        loadComponent: () =>
          import('./features/absences/absence-type-edit.page').then((m) => m.AbsenceTypeEditPage)
      },
      {
        path: 'assenze',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ABSENCES_READ,
          features: ['ABSENCES']
        },
        loadComponent: () =>
          import('./features/absences/absences-list.page').then((m) => m.AbsencesListPage)
      },
      {
        path: 'assenze/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ABSENCES_CREATE,
          features: ['ABSENCES']
        },
        loadComponent: () =>
          import('./features/absences/absence-create.page').then((m) => m.AbsenceCreatePage)
      },
      {
        path: 'assenze/:absenceId',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ABSENCES_READ,
          features: ['ABSENCES']
        },
        loadComponent: () =>
          import('./features/absences/absence-detail.page').then((m) => m.AbsenceDetailPage)
      },
      {
        path: 'assenze/:absenceId/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['ABSENCES', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'ABSENCE',
            paramKey: 'absenceId',
            title: 'Allegati Assenza'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'assenze/:absenceId/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['ABSENCES', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'ABSENCE',
            paramKey: 'absenceId',
            title: 'Nuovo Allegato Assenza'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'assenze/:absenceId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ABSENCES_UPDATE,
          features: ['ABSENCES']
        },
        loadComponent: () =>
          import('./features/absences/absence-edit.page').then((m) => m.AbsenceEditPage)
      },
      {
        path: 'segnalazioni',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_REQUESTS_READ,
          features: ['REQUESTS_MODULE']
        },
        loadComponent: () =>
          import('./features/requests/requests-list.page').then((m) => m.RequestsListPage)
      },
      {
        path: 'segnalazioni/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_REQUESTS_CREATE, features: ['REQUESTS_MODULE'] },
        loadComponent: () =>
          import('./features/requests/request-create.page').then((m) => m.RequestCreatePage)
      },
      {
        path: 'segnalazioni/:requestId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_REQUESTS_UPDATE, features: ['REQUESTS_MODULE'] },
        loadComponent: () =>
          import('./features/requests/request-edit.page').then((m) => m.RequestEditPage)
      },
      {
        path: 'segnalazioni/:requestId/risposte/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_REQUESTS_REPLY_CREATE,
          features: ['REQUESTS_MODULE']
        },
        loadComponent: () =>
          import('./features/requests/request-reply-create.page').then((m) => m.RequestReplyCreatePage)
      },
      {
        path: 'segnalazioni/:requestId',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_REQUESTS_READ,
          features: ['REQUESTS_MODULE']
        },
        loadComponent: () =>
          import('./features/requests/request-detail.page').then((m) => m.RequestDetailPage)
      },
      {
        path: 'segnalazioni/:requestId/allegati',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_READ,
          features: ['REQUESTS_MODULE', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'REQUEST',
            paramKey: 'requestId',
            title: 'Allegati Segnalazione'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'segnalazioni/:requestId/allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: {
          access: FEATURE_ACCESS_ATTACHMENTS_CREATE,
          features: ['REQUESTS_MODULE', 'ATTACHMENTS_MODULE'],
          attachmentsContext: {
            entityType: 'REQUEST',
            paramKey: 'requestId',
            title: 'Nuovo Allegato Segnalazione'
          }
        },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'allegati',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ATTACHMENTS_READ, features: ['ATTACHMENTS_MODULE'] },
        loadComponent: () =>
          import('./features/attachment/attachments-list.page').then((m) => m.AttachmentsListPage)
      },
      {
        path: 'allegati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ATTACHMENTS_CREATE, features: ['ATTACHMENTS_MODULE'] },
        loadComponent: () =>
          import('./features/attachment/attachment-create.page').then((m) => m.AttachmentCreatePage)
      },
      {
        path: 'allegati/:attachmentId',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ATTACHMENTS_READ, features: ['ATTACHMENTS_MODULE'] },
        loadComponent: () =>
          import('./features/attachment/attachment-detail.page').then((m) => m.AttachmentDetailPage)
      },
      {
        path: 'statistiche/timbrature',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_STATS_WORKS_READ, features: ['BASIC_ANALYTICS'] },
        loadComponent: () =>
          import('./features/stats/works-stats.page').then((m) => m.WorksStatsPage)
      },
      {
        path: 'statistiche/assenze',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_STATS_ABSENCES_READ, features: ['BASIC_ANALYTICS'] },
        loadComponent: () =>
          import('./features/stats/absences-stats.page').then((m) => m.AbsencesStatsPage)
      },
      {
        path: 'statistiche/report-avanzati',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ADVANCED_REPORTS_READ, features: ['ADVANCED_REPORTS'] },
        loadComponent: () =>
          import('./features/stats/advanced-reports-list.page').then((m) => m.AdvancedReportsListPage)
      },
      {
        path: 'statistiche/report-avanzati/nuovo',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ADVANCED_REPORTS_READ, features: ['ADVANCED_REPORTS'] },
        loadComponent: () =>
          import('./features/stats/advanced-reports.page').then((m) => m.AdvancedReportsPage)
      },
      {
        path: 'statistiche/report-avanzati/:reportId/modifica',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_ADVANCED_REPORTS_READ, features: ['ADVANCED_REPORTS'] },
        loadComponent: () =>
          import('./features/stats/advanced-reports.page').then((m) => m.AdvancedReportsPage)
      },
      {
        path: 'statistiche/dashboards',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SELF_BI_READ, features: ['SELF_BI'] },
        loadComponent: () =>
          import('./features/stats/advanced-dashboards-list.page').then((m) => m.AdvancedDashboardsListPage)
      },
      {
        path: 'statistiche/dashboards/nuova',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SELF_BI_MANAGE, features: ['SELF_BI'] },
        loadComponent: () =>
          import('./features/stats/advanced-dashboards-create.page').then((m) => m.AdvancedDashboardsCreatePage)
      },
      {
        path: 'statistiche/dashboards/:dashboardId/preview',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SELF_BI_READ, features: ['SELF_BI'] },
        loadComponent: () =>
          import('./features/stats/advanced-dashboards-preview.page').then((m) => m.AdvancedDashboardsPreviewPage)
      },
      {
        path: 'statistiche/dashboards/:dashboardId',
        canActivate: [permissionGuard, featureGuard],
        data: { access: FEATURE_ACCESS_SELF_BI_READ, features: ['SELF_BI'] },
        loadComponent: () =>
          import('./features/stats/advanced-dashboards.page').then((m) => m.AdvancedDashboardsPage)
      },
      {
        path: 'log-audit',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.audit_logs.read', 'tenant.audit_logs.read'] } },
        loadComponent: () =>
          import('./features/audit/audit-logs-list.page').then((m) => m.AuditLogsListPage)
      },
      {
        path: 'log-audit/:auditLogId',
        canActivate: [permissionGuard],
        data: { access: { anyOf: ['platform.audit_logs.read', 'tenant.audit_logs.read'] } },
        loadComponent: () =>
          import('./features/audit/audit-log-detail.page').then((m) => m.AuditLogDetailPage)
      }
    ]
  },
  {
    path: '403',
    loadComponent: () => import('./features/errors/forbidden.page').then((m) => m.ForbiddenPage)
  },
  {
    path: '404',
    loadComponent: () => import('./features/errors/not-found.page').then((m) => m.NotFoundPage)
  },
  {
    path: '**',
    redirectTo: '404'
  }
];
